import React, { useState } from 'react';
import { X } from 'lucide-react';
import { mockData } from '../utils/mock';
import { useLanguage } from '../context/LanguageContext';
import { GalleryVideo } from './gallery/GalleryVideo';
import { Lightbox } from './gallery/Lightbox';

const GalleryModal = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  const [selectedImage, setSelectedImage] = useState(null);

  const openLightbox = (image, index) => setSelectedImage({ src: image, index });
  const closeLightbox = () => setSelectedImage(null);

  const navigateLightbox = (direction) => {
    if (!selectedImage) return;
    const currentIndex = selectedImage.index;
    const newIndex = direction === 'next'
      ? (currentIndex + 1) % mockData.gallery.length
      : (currentIndex - 1 + mockData.gallery.length) % mockData.gallery.length;
    setSelectedImage({ src: mockData.gallery[newIndex], index: newIndex });
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      style={{ background: 'rgba(10,10,10,0.92)', backdropFilter: 'blur(10px)' }}
      role="dialog"
      aria-modal="true"
      data-testid="gallery-modal"
    >
      <div className="min-h-screen py-10 px-4">
        <div className="mx-auto max-w-5xl">
          {/* Header */}
          <div className="flex justify-between items-start mb-10">
            <div>
              <div className="flex items-center" style={{ gap: '14px' }}>
                <span style={{ display: 'block', width: '32px', height: '1px', background: '#C9A84C' }} />
                <span className="font-body uppercase" style={{ fontSize: '11px', letterSpacing: '3px', color: '#C9A84C' }}>{t('nav.galeria')}</span>
              </div>
              <h1 className="font-display mt-3" style={{ fontSize: 'clamp(30px, 4vw, 44px)', fontWeight: 700, color: '#F5F5F0' }}>{t('gallery.title')}</h1>
            </div>
            <button onClick={onClose} className="text-[#C2C1B6] hover:text-[#C9A84C] transition-colors duration-200 p-2" data-testid="gallery-close-top" aria-label={t('gallery.close')}>
              <X className="w-7 h-7" />
            </button>
          </div>

          <GalleryVideo />

          {/* Photo Gallery */}
          {mockData.gallery.length > 0 && (
          <div>
            <div className="flex flex-col items-center text-center">
              <div className="flex items-center" style={{ gap: '14px' }}>
                <span style={{ display: 'block', width: '32px', height: '1px', background: '#C9A84C' }} />
                <span className="font-body uppercase" style={{ fontSize: '11px', letterSpacing: '3px', color: '#C9A84C' }}>{t('gallery.photosTitle')}</span>
              </div>
              <p className="font-body mt-3" style={{ fontSize: '14px', fontWeight: 300, color: '#C2C1B6' }}>{t('gallery.photosSubtitle')}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-8">
              {mockData.gallery.map((image, index) => (
                <button
                  key={`gallery-${image.slice(-20)}-${index}`}
                  className="group relative cursor-pointer overflow-hidden"
                  style={{ background: '#1C1C1C', border: '1px solid rgba(201,168,76,0.12)' }}
                  onClick={() => openLightbox(image, index)}
                  data-testid={`gallery-image-${index}`}
                >
                  <img
                    src={image}
                    alt={`Yamauchi Gallery ${index + 1}`}
                    loading="lazy"
                    decoding="async"
                    className="w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    style={{ height: '240px', filter: 'brightness(0.75)' }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: 'rgba(10,10,10,0.55)' }}>
                    <span className="font-body uppercase" style={{ fontSize: '11px', letterSpacing: '2px', color: '#C9A84C' }}>{t('gallery.viewImage')}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
          )}

          {/* Footer */}
          <div className="text-center mt-12">
            <button
              onClick={onClose}
              className="font-body uppercase inline-flex items-center transition-colors duration-200"
              style={{ border: '1px solid #C9A84C', color: '#C9A84C', padding: '13px 30px', fontSize: '12px', letterSpacing: '2px', fontWeight: 600 }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#C9A84C'; e.currentTarget.style.color = '#0A0A0A'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#C9A84C'; }}
              data-testid="gallery-close-bottom"
            >
              {t('gallery.close')}
            </button>
          </div>
        </div>
      </div>

      {selectedImage && (
        <Lightbox image={selectedImage} onNavigate={navigateLightbox} onClose={closeLightbox} />
      )}
    </div>
  );
};

const fullscreenStyles = `
  video:fullscreen { width: 100vw !important; height: 100vh !important; object-fit: contain !important; background: black !important; }
  video:-webkit-full-screen { width: 100vw !important; height: 100vh !important; object-fit: contain !important; background: black !important; }
  video:-moz-full-screen { width: 100vw !important; height: 100vh !important; object-fit: contain !important; background: black !important; }
  video:-ms-fullscreen { width: 100vw !important; height: 100vh !important; object-fit: contain !important; background: black !important; }
`;

if (typeof document !== 'undefined' && !document.getElementById('fullscreen-video-styles')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'fullscreen-video-styles';
  styleElement.textContent = fullscreenStyles;
  document.head.appendChild(styleElement);
}

export default GalleryModal;
